// libs/axios.js
import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'

// Evento global para avisar que la sesión murió. Lo escucha AuthProvider, que
// es el único que puede sacar al usuario; desde acá no navegamos.
export const EVENTO_SESION_MUERTA = 'auth:sesion-muerta'

const avisarSesionMuerta = () => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event(EVENTO_SESION_MUERTA))
}

const createInstance = baseURL => {
    const instance = axios.create({
        headers: { 'Content-Type': 'application/json' },
        baseURL,
        // ⏱️ Un 504 real (Lambda corta a 29s) debe aparecer como error de estado
        // y no como un cuelgue indefinido que invite a re-clicks del vendedor.
        timeout: 35000,
    })

    instance.interceptors.request.use(async config => {
        if (config.skipAuth) {
            return config
        }

        // 🧪 SOLO LOCAL — token dummy sin Cognito (gated por REACT_APP_LOCAL_AUTH).
        // El backend local (LOCAL_AUTH_BYPASS) lo acepta sin validar firma.
        if (process.env.REACT_APP_LOCAL_AUTH === 'true') {
            config.headers.Authorization = 'Bearer local-dev'
            if (config.data instanceof FormData) delete config.headers['Content-Type']
            return config
        }

        // ⚠️ NO usar forceRefresh aquí. El idToken dura 60 min (config del app
        // client de Cognito) y Amplify lo renueva solo cuando está por vencer.
        // Forzar el refresh en CADA request metía un InitiateAuth a Cognito por
        // llamada: sumaba latencia a todo y, con varias requests en paralelo
        // (una carga de página dispara 5-10), los refresh concurrentes se
        // pisaban → el catch dejaba `tokens` vacío → la request salía SIN
        // Authorization → 401 "El token de acceso no es válido".
        const { tokens } = await fetchAuthSession().catch(() => ({}))
        const idToken = tokens?.idToken

        if (idToken) {
            config.headers.Authorization = `Bearer ${idToken.toString()}`
        }

        // 🆕 Si es FormData, dejar que el navegador establezca Content-Type
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        return config
    })

    instance.interceptors.response.use(
        res => res.data,
        async error => {
            const config = error.config
            // ⚠️ NO reintentar POSTs no idempotentes (crean orden / inician o
            // confirman pago / crean boletas+inscripciones). Un replay automático
            // (429/503) duplicaba órdenes, pagos y boletas. Cubre online,
            // matrícula y presencial (todos usan esta misma instancia axios).
            const metodo = (config?.method || 'get').toLowerCase()
            const rutaSensible = /\/(ordenes\/crear|pagos\/iniciar|matriculas\/pago\/(crear|confirmar)|matriculas\/manual|ventas-presenciales\/iniciar|pago-pos|confirmar-gratuita|pago-webpay\/(confirmar|iniciar)|admin\/comunicaciones\/[^/]+\/(encolar|prueba))(\/|$)/.test(config?.url || '')
            const esPostSensible = metodo === 'post' && rutaSensible

            // 401: en el backend TODOS los 401 son guardas previas — los
            // middlewares authentication/authorization, y los `if (!user)` al
            // inicio de los controladores. Ninguno ocurre después de crear
            // órdenes, pagos ni boletas, así que reintentar es seguro incluso
            // en los POST sensibles de arriba. Un solo reintento, con refresh
            // forzado, para el token vencido o ausente. Si vuelve a fallar, el
            // error sube y la UI avisa.
            if (error.response?.status === 401 && config && !config._authRetry && !config.skipAuth) {
                config._authRetry = true
                try {
                    await fetchAuthSession({ forceRefresh: true })
                    return instance(config)
                } catch (_) {
                    avisarSesionMuerta()
                    throw error
                }
            }

            // Un 401 que sobrevive al reintento = la sesión de Cognito ya no
            // existe. Avisamos para que el AuthProvider saque al usuario en vez
            // de dejarlo en el limbo: la pestaña seguía creyendose logueada y
            // polleando sin token para siempre.
            if (error.response?.status === 401 && config?._authRetry) {
                avisarSesionMuerta()
            }

            // Retry automático en 429 (límite de concurrencia) y 503
            if (
                (error.response?.status === 429 || error.response?.status === 503) &&
                !esPostSensible &&
                !config._retryCount
            ) {
                config._retryCount = 0
            }
            if (
                (error.response?.status === 429 || error.response?.status === 503) &&
                !esPostSensible &&
                config._retryCount < 3
            ) {
                config._retryCount += 1
                const delay = 1000 * config._retryCount  // 1s, 2s, 3s
                await new Promise(res => setTimeout(res, delay))
                return instance(config)
            }
            throw error
        }
    )

    return instance
}

export default createInstance
